<template>
  <div v-if="isModalOpen" class="fixed inset-0 z-60 flex items-center justify-center">
    <div v-if="modalType !== 'inventory_change'" class="fixed inset-0 bg-black bg-opacity-60" @click="closeModal"></div>
    <div v-else class="fixed inset-0 bg-transparent" @click="closeModal"></div>
    
    <!-- Regular Modal Layout -->
    <div v-if="modalType === 'default'" class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-4">
      <div class="p-6">
        <h3 class="text-lg font-semibold mb-4">{{ modalTitle }}</h3>
        <p class="text-gray-600 mb-6" v-html="modalMessage"></p>
        <div class="flex justify-center">
          <button
            @click="closeModal"
            :style="{
              backgroundColor: tourSettings?.page_action_color ?? '#000000',
              color: tourSettings?.page_action_text_color ?? '#ffffff'
            }"
            :class="[
              'flex justify-center items-center mx-auto min-w-[6rem] my-5 py-2 px-6 rounded-full',
              disabled ? 'opacity-40' : 'opacity-100'
            ]"
          >
            {{ $t('ok', 'OK') }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Inventory Change Modal Layout -->
    <!-- To change number of flip rounds: modify animationRounds in data() or use setAnimationRounds() method -->
    <!-- To change movement delay: modify movementDelay in data() or use setMovementDelay() method -->
    <div v-else-if="modalType === 'inventory_change'" 
         :ref="'modalContainer'"
         class="modal-container relative bg-white rounded-lg shadow-lg w-3/5 mx-auto p-8 flex items-center justify-center max-w-lg min-w-80">
      <!-- Centered Image with Quantity Badge -->
      <div v-if="modalData?.changes && modalData.changes.length > 0" class="flex flex-col items-center justify-center">
        <div v-for="(change, index) in modalData.changes" :key="index" 
             :ref="`changeItem-${index}`"
             class="inventory-change-item relative flex flex-col items-center justify-center"
             :class="{ 'animating': changeAnimating[index] }">
          
          <!-- Item Image with Quantity Overlay -->
          <div class="relative">
            <img v-if="change.item.image" 
                 :src="change.item.image?.preview_url" 
                 :alt="change.item.name"
                 :class="[
                   'item-image w-24 h-24 object-cover rounded-lg border-2 border-gray-200',
                   { 'spinning': changeAnimating[index] }
                 ]" />
            <div v-else 
                 :class="[
                   'item-image w-24 h-24 bg-gray-200 rounded-lg border-2 border-gray-300 flex items-center justify-center',
                   { 'spinning': changeAnimating[index] }
                 ]">
              <svg class="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            
            <!-- Quantity Change Badge (hidden during movement) -->
            <div v-if="!isMoving[index]" class="absolute -top-3 -right-3 bg-white rounded-full border-2 shadow-lg px-2 py-1 min-w-[2rem] text-center">
              <span class="font-bold text-sm"
                    :class="change.operation === 'add' ? 'text-green-600' : 'text-red-600'">
                {{ change.operation === 'add' ? '+' : '-' }}{{ (change.quantity > 0) ? change.quantity : "" }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Locked Point Modal Layout -->
    <div v-else-if="modalType === 'locked_point'" class="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-4">
      <div class="flex items-center justify-center p-6 border-b border-gray-200">
        <div class="flex-shrink-0">
          <svg class="h-12 w-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <div class="ml-4">
          <h3 class="text-xl font-bold text-gray-900">{{ modalTitle }}</h3>
        </div>
      </div>
      
      <!-- Content -->
      <div class="p-6">
        <div class="text-gray-700 mb-6">{{ modalMessage }}</div>
        
        <!-- Requirements Section -->
        <div v-if="modalData?.unmetConditions && modalData.unmetConditions.length > 0" class="mb-6">
          <div class="space-y-2">
            <div v-for="(condition, index) in modalData.unmetConditions" :key="index" 
                 class="flex items-center p-3">
              <div class="flex-shrink-0 mr-3">
                <svg class="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div class="flex-1">
                <span class="font-medium text-red-800">
                  {{ formatCondition(condition) }}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Action buttons -->
        <div class="flex justify-center">
          <button
            @click="closeModal"
            class="font-medium min-w-[6rem] text-center justify-center items-center mx-auto my-5 py-2 px-6 rounded-full"
            :style="{
              backgroundColor: tourSettings?.page_action_color ?? '#000000',
              color: tourSettings?.page_action_text_color ?? '#ffffff'
            }"
          >
            {{ $t('ok', 'OK') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { showMultiLingualText } from '../utils/languageUtils'

export default {
  name: 'VueModal',
  data() {
    return {
      changeAnimating: {},
      isMoving: {},
      isAnimating: false,
      animationRounds: 1, // Number of complete vertical flips (change this value to adjust rounds)
      movementDelay: 1000 // Delay in milliseconds between rotation and movement (change this value to adjust delay)
    }
  },
  computed: {
    ...mapGetters('modal', ['isModalOpen', 'modalTitle', 'modalMessage', 'modalType', 'modalData']),
    ...mapGetters('settings', ['tourSettings'])
  },
  watch: {
    modalData: {
      handler(newData) {
        if (newData?.changes && this.modalType === 'inventory_change') {
          // Reset any previous hidden states
          this.resetModalState()
          this.startAnimations()
        }
      },
      immediate: true
    }
  },
  methods: {
    closeModal() {
      this.$store.dispatch('modal/closeModal')
    },
    showMultiLingualText(text, altText = null, showFirstLanguage = false) {
      return showMultiLingualText(text, altText, showFirstLanguage, this.$i18n.locale)
    },
    formatCondition(condition) {
      // Handle API condition structure
      if (condition) {
        const { target, operator, value, locked_message } = condition
        
        // Handle variable conditions
        if (condition.target_type === 'variable' && target?.name) {
          const operatorSymbol = this.getOperatorSymbol(operator)
          if (locked_message) {
            return this.showMultiLingualText(locked_message)
          }
          return `${target.name} ${operatorSymbol} ${value}`
        }
        
        // Handle object/inventory conditions
        if (condition.target_type === 'object' && target?.name) {
          if (operator === 'eq') {
            return `Must have exactly ${value} ${target.name}`
          } else if (operator === 'gte') {
            return `Must have at least ${value} ${target.name}`
          } else if (operator === 'lte') {
            return `Must have at most ${value} ${target.name}`
          } else if (operator === 'gt') {
            return `Must have more than ${value} ${target.name}`
          } else if (operator === 'lt') {
            return `Must have less than ${value} ${target.name}`
          } else if (operator === 'has') {
            return `Must have: ${target.name}`
          } else if (operator === 'not_has') {
            return `Must not have: ${target.name}`
          } else {
            return `${target.name} ${operator} ${value}`
          }
        }
      }
      
      // Fallback for string conditions
      if (typeof condition === 'string') {
        return condition
      }
      
      // Fallback for unknown format
      return JSON.stringify(condition)
    },
    getOperatorSymbol(operator) {
      const operatorMap = {
        'gte': '≥',
        'lte': '≤',
        'gt': '>',
        'lt': '<',
        'eq': '=',
        'ne': '≠'
      }
      return operatorMap[operator] || operator
    },
    getTotalInventoryItems() {
      if (!this.modalData?.gameState?.inventory) return 0
      return Object.values(this.modalData.gameState.inventory).reduce((total, item) => total + (item.quantity || 0), 0)
    },
    
    // Method to configure animation rounds (optional - you can also change animationRounds directly)
    setAnimationRounds(rounds) {
      this.animationRounds = rounds
    },
    
    // Method to configure movement delay (optional - you can also change movementDelay directly)
    setMovementDelay(delayMs) {
      this.movementDelay = delayMs
    },
    
    // Reset modal state to ensure clean start
    resetModalState() {
      this.isAnimating = false
      this.changeAnimating = {}
      this.isMoving = {}
      
      // Remove any hidden classes from previous animations
      this.$nextTick(() => {
        const modalContainer = this.$refs.modalContainer
        if (modalContainer) {
          modalContainer.classList.remove('hidden', 'move-up', 'move-down')
          modalContainer.style.opacity = ''
          modalContainer.style.visibility = ''
        }
      })
    },
    
    startAnimations() {
      if (!this.modalData?.changes) return
      
      this.isAnimating = true
      this.changeAnimating = {}
      this.isMoving = {}
      
      // Start animations for each change item
      this.modalData.changes.forEach((change, index) => {
        this.changeAnimating[index] = true
        this.isMoving[index] = false
        
        // Start spinning animation
        this.$nextTick(() => {
          const itemElement = this.$refs[`changeItem-${index}`]?.[0]
          if (itemElement) {
            // Set CSS custom property for number of rounds
            const imageElement = itemElement.querySelector('.item-image')
            if (imageElement) {
              imageElement.style.setProperty('--flip-rounds', this.animationRounds)
            }
            
            // Calculate timing based on rounds (1 second per round) + movement delay
            const animationDuration = (this.animationRounds * 1000) + this.movementDelay
            
            // After specified rounds + delay, start movement animation
            setTimeout(() => {
              this.startMovementAnimation(change.operation, index)
            }, animationDuration)
          }
        })
      })
    },
    
    startMovementAnimation(operation, index) {
      // Hide quantity badge during movement
      this.isMoving[index] = true
      
      // Get the modal container
      const modalContainer = this.$refs.modalContainer
      if (!modalContainer) return
      
      // Add movement class based on operation to the entire modal
      const movementClass = operation === 'add' ? 'move-up' : 'move-down'
      modalContainer.classList.add(movementClass)
      
      // After movement animation completes, reset and close modal
      setTimeout(() => {
        this.resetAndClose(index)
      }, 1000) // Movement animation duration
    },
    
    resetAndClose(index) {
      // Reset animation state
      this.changeAnimating[index] = false
      this.isMoving[index] = false
      
      // Check if all animations are complete
      const allComplete = this.modalData.changes.every((_, idx) => !this.changeAnimating[idx])
      
      if (allComplete) {
        this.isAnimating = false
        
        // Hide modal container and remove movement classes
        const modalContainer = this.$refs.modalContainer
        if (modalContainer) {
          modalContainer.classList.add('hidden')
          modalContainer.classList.remove('move-up', 'move-down')
        }
        
        // Close modal immediately after hiding content
        this.closeModal()
      }
    }
  }
}
</script>

<style scoped>
/* Vertical flip animation for item images */
.item-image.spinning {
  animation: verticalFlip 1s linear;
  animation-iteration-count: var(--flip-rounds, 2);
}

@keyframes verticalFlip {
  0% {
    transform: rotateY(0deg);
  }
  25% {
    transform: rotateY(90deg);
  }
  50% {
    transform: rotateY(180deg);
  }
  75% {
    transform: rotateY(270deg);
  }
  100% {
    transform: rotateY(360deg);
  }
}

/* Movement animations for modal container */
.modal-container.move-up {
  animation: moveUp 1s ease-in-out forwards;
}

.modal-container.move-down {
  animation: moveDown 1s ease-in-out forwards;
}

@keyframes moveUp {
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(-100vh);
    opacity: 0;
  }
}

@keyframes moveDown {
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh);
    opacity: 0;
  }
}

/* Ensure smooth transitions */
.inventory-change-item {
  transition: all 0.3s ease;
}

/* Quantity badge styling */
.absolute.-top-3.-right-3 {
  z-index: 10;
  border-color: #e5e7eb;
}

.absolute.-top-3.-right-3 span {
  line-height: 1;
}

/* Center the modal content */
.inventory-change-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Ensure proper perspective for 3D rotation */
.item-image {
  transform-style: preserve-3d;
}

/* Hide content during modal close transition */
.inventory-change-item.hidden,
.modal-container.hidden {
  opacity: 0 !important;
  visibility: hidden !important;
  transition: opacity 0.1s ease, visibility 0.1s ease;
}

/* Modal styling for inventory changes */
.inventory-change-modal {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  min-height: 200px;
  min-width: 200px;
}

/* Ensure modal content is properly sized */
.inventory-change-item {
  min-height: 120px;
  min-width: 120px;
}

/* Message-like modal styling */
.inventory-change-modal {
  transition: all 0.3s ease;
  transform: scale(1);
}

/* Responsive design for smaller screens */
@media (max-width: 640px) {
  .inventory-change-modal {
    width: 90% !important;
    min-width: 280px;
  }
}
</style> 